//Framework
import { Fragment, useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import cx from 'classnames';
import moment, { Moment } from 'moment';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { useAppSelector } from '../../app/hooks';
import DatePicker from 'react-datepicker';
//Services
import { getTimesheetStatuses, submitTimesheet } from '../../api/services/timesheetService';
import { batchUpdateCaseloads, searchCaseloads } from '../../api/services/caseloadService';
import { getTimesheetDistricts } from '../../api/services/districtService';
import { getTherapistDistrictProgramOptions } from '../../api/services/districtService';

//Types/Data
import { EntryTypes, TimesheetUnlockStatusLookup, TimesheetStatus } from '../../common/constants';
import TimesheetWeek from '../../api/models/timesheetWeek';
import Discipline from '../../api/models/discipline';
import TimesheetSummary from '../../api/models/timesheetSummary';
import TimesheetSubmitRequest from '../../api/requests/timesheetSubmitRequest';
import TimesheetStatusesResponse from '../../api/responses/timesheetStatusesResponse';
import TimesheetEntryFlag from '../../api/models/timesheetEntryFlag';
import WeekItem from '../../api/models/timesheetWeekItem';
import TimesheetFlagNote from '../../api/models/timesheetFlagNote';
import DistrictProgramOptionsResponse from '../../api/responses/districtProgramOptionsResponse';
import TimesheetFlag from '../../api/models/timesheetFlag';
import caseloadDisplay from '../../api/models/caseloadDisplay';
import CaseloadsResponse from '../../api/responses/caseloadsResponse';

//Components
import Layout from '../../components/layout';
import Modal from '../../components/modal';
import DisciplineModal from '../../components/disciplineModal';
import LogtimeModal from '../../components/logTimeModal';
import TimesheetWeekEntries from '../../components/timesheetWeekEntries';
import DischargeModal from '../../components/dischargeModal';
import { Notification } from '../../components/notifications';

//Utils
import { daysOfWeek, dynamicSort, hasValue } from '../../common/utils';
import { getStudentsFromTimesheet, getStudentsFromCaseloads } from '../../common/studentUtils';
import DistrictProgramOption from '../../api/models/districtProgramOption';
import TimesheetDistrict from '../../api/models/timesheetDistrict';
import TimesheetDistrictsResponse from '../../api/responses/timesheetDistrictsWrap';
import EditEntry from '../../components/logTimeModal/types/editEntry';
import TaskType from '../../api/models/tasktype';
import { getTaskTypes } from '../../api/services/taskTypeService';
import TimesheetGroupedDistrictItem from '../../api/models/TimesheetGroupedDistrictItem';
import TimesheetGroupedSchoolItem from '../../api/models/TimesheetGroupedSchoolItem';
import TimesheetReviewGroupedItem from '../../api/models/TimesheetReviewGroupedItem';
import TimesheetGroupTitle from '../../api/models/TimesheetGroupTitle';
import TimesheetUnlockRequestData from '../../api/requests/timesheetUnlockRequestData';
import { requestTimesheetUnlock } from '../../api/services/timesheetUnlockService';
import TimesheetUnlockRequestModel from '../../api/models/timesheetUnlockRequestModel';
import CustomAlert from '../../components/alert/commonAlert';
import $ from "jquery";
import { setTimeout } from 'timers';

export type SelectedRange = {
  startDate: Date;
  endDate: Date;
  week?: TimesheetWeek;
};

type ViewState = {
  discipline?: Discipline;
  disciplineId?: number
};

const getTimesheetsDefer = async (args: any[]) => {
  return (typeof args[0].disciplineId !== "undefined" && args[0].disciplineId !== -1)
    ? getTimesheetStatuses(args[0].disciplineId, args[0].startDate.toDateString(), args[0].endDate.toDateString())
    : null;
}

const getDistrictsPromis = async () => {
  return getTimesheetDistricts();
}

const getDistrictProgramOptionsPromise = async (args: any) => {
  return getTherapistDistrictProgramOptions(args[0], args[1], args[2]);
}

let taskTypesCache: TaskType[] = [];
const loadTaskTypes = async () => {
  if (typeof taskTypesCache !== "undefined" && taskTypesCache.length > 0)
    return taskTypesCache;

  taskTypesCache = await (await getTaskTypes()).data?.taskTypes?.filter(t => t.isVisible) ?? [];
  return taskTypesCache;
}

export const getCurrentWeekDates = () => {

  const today = new Date();
  const currentDate = today.getDate();
  const currentDay = today.getDay();

  const mondayDate = new Date(today);
  mondayDate.setDate(currentDate - (currentDay - 1));

  const sundayDate = new Date(mondayDate);
  sundayDate.setDate(mondayDate.getDate() + 6);

  return {
    monday: mondayDate,
    sunday: sundayDate,
  };
};

/**
 * Page for displaying timesheets and entering time
 * @returns 
 */
const Timesheets = () => {
  const now = new Date();
  const { state } = useLocation<ViewState>();
  const location = useLocation();
  const parsedQueryString = queryString.parse(location.search);
  const urlYear = Number(parsedQueryString.year);
  const urlMonth = Number(parsedQueryString.month);
  const urlWeekId = Number(parsedQueryString.weekId);

  const { monday, sunday } = getCurrentWeekDates();
  const [startDate, setStartDate] = useState<Date | null>(monday);
  const [endDate, setEndDate] = useState<Date | null>(sunday);

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>();

  // UI States
  const [month, setMonth] = useState(urlMonth || now.getMonth() + 1);
  const [year, setYear] = useState(urlYear || now.getFullYear());
  const [openDateRangeFilter, setOpenDateRangeFilter] = useState(false);
  const [openDiscipline, setOpenDiscipline] = useState(false);
  const [openLogtime, setOpenLogtime] = useState(false);
  const [districts, setDistricts] = useState<TimesheetDistrict[]>();
  const [districtProgramOptions, setDistrictProgramOptions] = useState<DistrictProgramOption[]>();
  const [selectedDiscipline, setSelectedDiscipline] = useState<Discipline | undefined>(state && state.discipline ? state.discipline : undefined);
  const [selectedDisciplineId, setSelectedDisciplineId] = useState<number>(Number(parsedQueryString.disciplineId || state?.discipline?.disciplineId || -1));
  const [selectedRange, setSelectedRange] = useState<SelectedRange | null>();
  const [selectedWeek, setSelectedWeek] = useState<TimesheetWeek>();
  const [timesheets, setTimesheets] = useState<TimesheetSummary[]>();
  const [editEntry, setEditEntry] = useState<EditEntry>();
  const [reviewTimesheet, setReviewTimesheet] = useState<TimesheetSummary>();
  const [isFetchingCaseload, setIsFetchingCaseload] = useState<boolean>(false);
  const [activeTimesheet, setActiveTimesheet] = useState<TimesheetSummary>();
  const [isReviewed, setIsReviewed] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [tsStatus, setTsStatus] = useState<number>(0);
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  const [flagNotes, setFlagNotes] = useState<TimesheetFlagNote[]>();
  const [showFlags, setShowFlags] = useState<boolean>(true);
  const userState = useAppSelector((userState) => userState);
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
  const [selectedTimesheet, setSelectedTimesheet] = useState<TimesheetSummary | null>();

  const [isInReview, setIsInReview] = useState<boolean>(false);
  const [dischargeModal, setDischargeModal] = useState<boolean>(false);
  const [removeCaseloadModal, setRemoveCaseloadModal] = useState<boolean>(false);
  const [studentsWithNoTimeSubmitted, setStudentsWithNoTimeSubmitted] = useState<any[]>([]);

  const [keepCaseloads, setKeepCaseloads] = useState<caseloadDisplay[]>([]);
  const [dischargeCaseloads, setDischargeCaseloads] = useState<caseloadDisplay[]>([]);
  const [dischargeCaseload, setDischargeCaseload] = useState<caseloadDisplay>();
  const [dischargeCaseloadEndDate, setDischargeCaseloadEndDate] = useState<Date>(new Date());
  const [removeCaseload, setRemoveCaseload] = useState<caseloadDisplay>();
  const [removeCaseloads, setRemoveCaseloads] = useState<caseloadDisplay[]>([]);
  const [removeCaseloadEndDate, setRemoveCaseloadEndDate] = useState<Date>(new Date());
  const [timesheetFeedback, setTimesheetFeedback] = useState<string>('');
  const [timesheetFeedbackUser, setTimesheetFeedbackUser] = useState<string>('');
  const [timesheetFeedbackDate, setTimesheetFeedbackDate] = useState<Date>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [allTaskTypes, setAllTaskTypes] = useState<TaskType[]>([]);
  const [saveWeek, setSaveWeek] = useState<boolean>(true);
  const [resetLogtimeModal, setResetLogtimeModal] = useState<boolean>(true);
  const [groupedWeek, setGroupedWeek] = useState<TimesheetGroupedDistrictItem<TimesheetReviewGroupedItem>[]>();
  const endOfCurrentWeek = moment().endOf('week').add(-1, 'day').startOf('day');
  const [totalHours, setTotalHours] = useState<number | undefined>();
  const [taskTypeFound, setTaskTypeFound] = useState<boolean | undefined>(false);
  const [isLocked, setIsLocked] = useState<boolean>(false);
  const lockDateOffset = 1;
  const lockTimeOffset = process.env.REACT_APP_UNLOCK_REQUEST_TIME_OFFSET;

  const setOverviewHeader = () => {
    if (reviewTimesheet) {
      let container = $("#timesheetOverviewContainer");
      let scrollablePart = $(".overviewScrollablePart");
      let containerHeight = container.height();
      let scrollableHeight = containerHeight! * 0.5;

      scrollablePart.addClass(`clear-both pt-4 mt-4 h-6/12 overflow-hidden overflow-y-scroll scrollbar`)
      scrollablePart.css("maxHeight", `${scrollableHeight}px`);
    }
  }

  window.onresize = function () {
    if (reviewTimesheet) {
      let container = $("#timesheetOverviewContainer");
      let scrollablePart = $(".overviewScrollablePart");
      let containerHeight = container.height();
      let scrollableHeight = containerHeight! * 0.5;

      scrollablePart.css("maxHeight", `${scrollableHeight}px`);
    }
  }

  const createGroupedWeekItemsModel = (): TimesheetGroupedDistrictItem<TimesheetReviewGroupedItem>[] => {
    let groupedWeek: TimesheetGroupedDistrictItem<TimesheetReviewGroupedItem>[] = [];
    reviewTimesheet?.weeks.forEach(week => {
      week?.items.forEach(item => {
        let programTitles: TimesheetGroupTitle[] = [];

        let programTasks = item.tasks.filter(x => x.programName);
        if (programTasks.length > 0) {
          programTasks.forEach(t => {
            if (!programTitles.find(x => x.name === t.programName))
              programTitles.push({ name: t.programName.toString(), isProgram: true });
          })
        }

        if (programTasks.length !== item.tasks.length)
          programTitles.push({ name: item.description, isProgram: false });

        programTitles.forEach(pt => {
          var districtGroup = groupedWeek.find(x => x.title === pt.name);
          if (!districtGroup) {
            let districtTemp: TimesheetGroupedDistrictItem<TimesheetReviewGroupedItem> = {
              title: pt.name.toString(),
              districtId: item.districtId,
              schools: [],
            }

            districtGroup = districtTemp;
            groupedWeek.push(districtGroup);
          }

          var schoolGroup = districtGroup.schools?.find(x => x.title === item.subTitle);
          if (!schoolGroup) {
            let schoolTemp: TimesheetGroupedSchoolItem<TimesheetReviewGroupedItem> = {
              title: item.subTitle,
              items: []
            }

            schoolGroup = schoolTemp;
            districtGroup.schools?.push(schoolGroup);
          }

          item.tasks.filter(x => x.programName === (pt.isProgram ? pt.name : null)).forEach(task => {
            task.entries.forEach(entry => {
              let itemTemp: TimesheetReviewGroupedItem = {
                date: entry.dateOfEntry,
                dateString: moment(entry.dateOfEntry).format('dddd MM/DD'),
                minutes: entry.minutes ?? 0,
                notes: entry.notes,
                entry: task.entryTypeId === EntryTypes.Group ? item.title : task.taskTypeName.toString(),
                entryType: task.entryTypeName.toString()
              }

              if (task.entryTypeId === EntryTypes.Student) {
                itemTemp.entry += ` - ${item.title}`;
              }

              schoolGroup?.items?.push(itemTemp);
            })
          })

          schoolGroup?.items?.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        })
      })
    })

    return groupedWeek;
  }

  useEffect(() => {
    setTaskTypeFound(false);
    selectedTimesheet?.weeks[0].items.map((entry) => {
      if (entry.tasks[0].taskTypeName.includes("Absence") || entry.tasks[0].taskTypeName.includes("Unavailable")) {
        setTaskTypeFound(true);
      }
    })

    setGroupedWeek(createGroupedWeekItemsModel());
  }, [reviewTimesheet])

  /**
   * Get all task types
   */
  useAsync({
    promiseFn: loadTaskTypes,
    onResolve: (res) => setAllTaskTypes(res)
  });

  const loadDistricts = useAsync({
    promiseFn: getDistrictsPromis,
    onResolve: (response: TimesheetDistrictsResponse) => setDistricts(response.data?.districts?.filter(x => x.isVisible) ?? [])
  });

  const loadDistrictProgramOptions = useAsync({
    deferFn: getDistrictProgramOptionsPromise,
    onResolve: (response: DistrictProgramOptionsResponse) => {
      if (response) {
        setDistrictProgramOptions(response.data?.districts);
      }
    },
  });

  useEffect(() => {
    const disciplineId: number | string | null = selectedDisciplineId ?? localStorage.getItem('disciplineId') ?? null;

    if (disciplineId && Number(disciplineId) < 0 && isFirstLoad) {
      setIsFirstLoad(false);
      return;
    }

    loadDistrictProgramOptions.run(month, year, disciplineId);
    timesheetsRequest.run({ disciplineId: disciplineId, startDate: startDate, endDate: endDate });
  }, [selectedDisciplineId]);

  const refreshTimesheetSelection = (response: TimesheetStatusesResponse | null, saveWeek: boolean = true) => {
    let targetStartDate = selectedRange?.startDate || null;
    let targetEndDate = selectedRange?.endDate || null;
    let targetWeekId = selectedRange?.week?.weekId || urlWeekId;

    let week: TimesheetWeek | undefined = undefined;

    //loop through all timesheets and find the week entry
    response?.data?.timesheets?.forEach(timesheet => {
      if (selectedWeek) {
        setSelectedTimesheet(timesheet);
        setIsLocked(false);
        if (moment(timesheet!.end).startOf('day').add(lockDateOffset, 'days').add(lockTimeOffset, 'hours') < moment.utc() && timesheet.status?.timesheetStatusId != TimesheetStatus.Approved) {
          if (timesheet.timesheetId == null) {
            setIsLocked(true)
          } else {
            setIsLocked(timesheet.isLocked!);
          }
        }
        let foundWeek = timesheet.weeks?.find(x => x.firstDayOfWeek === selectedWeek.firstDayOfWeek && x.lastDayOfWeek === selectedWeek.lastDayOfWeek);
        if (foundWeek) {
          week = foundWeek;
          return;
        }
      } else {
        let foundWeek = timesheet.weeks?.find(w => w.weekId == targetWeekId ||
          (w.firstDayOfWeek == selectedRange?.week?.firstDayOfWeek && w.lastDayOfWeek == selectedRange?.week.lastDayOfWeek));
        if (foundWeek) {
          week = foundWeek;
          return;
        }
      }
    });

    if (week && saveWeek) {
      setSelectedRange({
        startDate: targetStartDate!,
        endDate: targetEndDate!,
        week: week,
      });
      setSelectedWeek(current => week);
    }
  }

  const getDistrictTotalTime = (schools: TimesheetGroupedSchoolItem<TimesheetReviewGroupedItem>[], weekendEnabled?: boolean) => {
    let total = 0;
    schools.forEach(s => {
      const items = weekendEnabled === false
        ? s.items?.filter(item => { const d = moment(item.date).isoWeekday(); return d !== 6 && d !== 7; })
        : s.items;
      total += items?.reduce((n, { minutes }) => n + minutes, 0) ?? 0;
    })

    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    return formatter.format(total / 60);
  }

  const timesheetsRequest = useAsync({
    deferFn: getTimesheetsDefer,
    onResolve: (response: TimesheetStatusesResponse | null) => {
      setOpenDateRangeFilter(false);
      const timesheets = response?.data?.timesheets ?? [];
      timesheets.map(timesheet =>
        timesheet.weeks.map(week =>
          week.items.map(item =>
            item.tasks.map(task =>
              task.entries.map(entry => entry.timesheetEntryId = (!entry.timesheetEntryId || entry.timesheetEntryId < 1 ? entry.entryId : entry.timesheetEntryId) ?? -1)
            ))));
      setTimesheets([...timesheets || []]);

      refreshTimesheetSelection(response);
    },
  });

  const timesheetsRequestNoRefresh = useAsync({
    deferFn: getTimesheetsDefer,
    onResolve: (response: TimesheetStatusesResponse | null) => {
      //setOpenDateRangeFilter(false);
      //setTimesheets([...response?.data?.timesheets || []]);


      //refreshTimesheetSelection(response);
    },
  });

  const handleUnlockRequestClick = async () => {
    if (!selectedTimesheet)
      return;

    const data: TimesheetUnlockRequestData = {
      disciplineId: selectedDisciplineId,
      timesheetId: selectedTimesheet?.timesheetId ?? 0,
      startDate: selectedTimesheet?.start!,
      endDate: selectedTimesheet?.end!
    }

    try {
      const response = await requestTimesheetUnlock(data)
      let respData = response?.data;

      if (respData && respData.success) {
        setSubmitMessage('Timesheet unlock requested!');
        setIsSubmitSuccess(true);
        setSelectedTimesheet(current => {
          const temp = current;
          let newRequest: TimesheetUnlockRequestModel = {
            timesheetUnlockRequestId: 0,
            timesheetId: selectedTimesheet.timesheetId! && respData.timesheetid!,
            timesheetUnlockStatus: TimesheetUnlockStatusLookup.Pending,
          };
          if (temp) {
            temp.timesheetUnlockRequests = temp.timesheetUnlockRequests ?? [];
            temp.timesheetUnlockRequests.push(newRequest);
          }

          return temp;
        })

        return new Promise<void>((resolve) => resolve());
      }

      setSubmitMessage(`Error requesting timesheet unlock: ${respData?.message}`);
      setIsSubmitSuccess(false);
    } catch (error: any) {
      setSubmitMessage(typeof error === 'string' ? error : 'An error occurred while requesting timesheet unlock.');
      setIsSubmitSuccess(false);
    }

    return new Promise<void>((resolve) => resolve());
  }

  const handleSubmit = async () => {
    //Create our status object
    const status = {
      timesheetStatusId: TimesheetStatus.Submitted ?? 0,
      name: TimesheetStatus[TimesheetStatus.Submitted]
    }

    //Create our submit request
    const request: TimesheetSubmitRequest = {
      timesheetId: reviewTimesheet?.timesheetId,
      timesheetStatus: status
    }

    try {
      //Submit the request
      const response = await submitTimesheet(request);

    //Make sure the response was successful
    if (response) {
      //Set notification message
      setSubmitMessage('Timesheet submitted!');
      setIsSubmitSuccess(true);

      //Reset the review timesheet to switch views
      setReviewTimesheet(undefined);
      setTaskTypeFound(false);
      //Clear out the selected date ranges so that the review timesheet does not appear after submission
      setSelectedRange(null);
      setSelectedTimesheet(null);
      setIsLocked(false);

      //Copy our timesheets array
      const sheets: TimesheetSummary[] = timesheets ?? [];
      let sheetIndex: number | null = null;

      //Loop through timesheets to find the one we want to replace
      sheets.map((ts, i) => {
        //If this is the correct sheet, save the index
        if (ts.timesheetId === request.timesheetId) {
          sheetIndex = i;
          return i;
        }
        return i;
      });

      //Verify we have a correct index
      if (sheetIndex !== null && sheets[sheetIndex] !== undefined && sheets[sheetIndex] !== null) {
        //Update the temporary timesheet array
        sheets[sheetIndex].status.timesheetStatusId = TimesheetStatus.Submitted ?? 0;
        sheets[sheetIndex].status.name = TimesheetStatus[TimesheetStatus.Submitted];

        //Update our timesheets array
        setTimesheets(sheets);
      }

      //refresh the timesheet data
      timesheetsRequest.run({ disciplineId: selectedDisciplineId, startDate: startDate, endDate: endDate });

      return new Promise<void>((resolve) => resolve());
    }

    setSubmitMessage('Error submitting timesheet');
    setIsSubmitSuccess(false);

    return new Promise<void>((resolve) => resolve());
    } catch (error: any) {
      setSubmitMessage(typeof error === 'string' ? error : 'An error occurred while submitting timesheet.');
      setIsSubmitSuccess(false);
    }
  }

  const handleSubmitAsync = useAsync({ deferFn: handleSubmit });

  const handleRequestTimesheetUnlockRequestAsync = useAsync({ deferFn: handleUnlockRequestClick });

  const handleSetDiscipline = (d: Discipline) => {
    setSelectedRange(null);
    setSelectedTimesheet(null);
    setIsLocked(false);
    setSelectedDiscipline(d);
    setSelectedDisciplineId(d.disciplineId!);
  }

  /**
 * Calculates the total hours logged based on the provided timesheet summary.
 *
 * @param {TimesheetSummary} timesheet - The timesheet summary to calculate the total hours from.
 * @return {number} The total hours logged as a decimal number rounded to two decimal places.
 */
  const getTotalHours = (timesheet: TimesheetSummary) => {

    let total = 0;
    timesheet.weeks.forEach((tw) => {
      tw.items.forEach((wi) => {
        wi.tasks.forEach((task) => {
          task.entries.forEach((entry) => {
            if (entry.minutes !== undefined && entry.minutes !== null && entry.minutes > 0)
              total += entry.minutes;
          });
        });
      });
    });

    let result = Number((total / 60).toFixed(2)).valueOf();
    setTotalHours(result);
    return result;
  }

  /**
   * Handles a click on the timesheet.
   * Argument is a week item
   * @param editEntry
   */
  const handleTimesheetFlagModal = (editEntry: any) => {
    //setShowFlags(false);
  }

  /**
     * Returns true if any of the entries in a given week are flagged
     * @param items 
     * @param day 
     * @returns boolean
     */
  const isWeekFlagged = (items: WeekItem[]) => {
    let flags: any[] = [];

    items.forEach((item) => {
      item.tasks.forEach((task) => {
        task.entries.forEach(entry => {
          if (entry && entry.timesheetEntryFlags && entry.timesheetEntryFlags.length > 0) {
            flags.push(...entry.timesheetEntryFlags);
          }
        });
      });
    });

    return flags.length > 0;
  }

  /**
   * Compares the active timesheet's students with the students on the
   * therapists caseload and returns all students who do not have time logged 
   **/
  const getStudentsWithNoTimeSubmitted = async (timesheet?: TimesheetSummary | undefined): Promise<caseloadDisplay[]> => {
    let caseloadIds: number[] = [];
    const caseloads: caseloadDisplay[] = [];

    try {
      setIsLoading(true);

      //Refrsh timesheets prior to calculation
      await getTimesheetsDefer([{ disciplineId: selectedDisciplineId, startDate: timesheet?.start ?? startDate, endDate: timesheet?.end ?? endDate }]);

      //Get the caseloads for the therapist
      const caseloadsResponse: CaseloadsResponse = await searchCaseloads(undefined, userState.user.id, undefined, undefined, undefined, undefined, selectedDisciplineId);
      const studentCaseloads: caseloadDisplay[] = caseloadsResponse.data.caseloads;

      //Get the students to compare
      const studentsLogged = getStudentsFromTimesheet(activeTimesheet! ?? timesheet ?? activeTimesheet!);
      const studentsOnCaseload = getStudentsFromCaseloads(studentCaseloads);

      //Loop through the ids and get the caseload data for each
      studentsOnCaseload.filter(student => !studentsLogged.includes(student)).forEach((id: number) => {
        //Get the caseload
        const caseload: caseloadDisplay | undefined = studentCaseloads.find(caseload =>
          caseload.studentId === id &&
          caseload.disciplineId === selectedDisciplineId
        );

        //If not undefined, then add it to the array
        if (caseload !== undefined) {
          caseloads.push(caseload);
        }
      });

      let sortedCaseloads = caseloads.sort(dynamicSort('studentFirstName')).sort(dynamicSort('studentLastName'));

      setKeepCaseloads([]);
      setRemoveCaseloads([]);
      setDischargeCaseloads([]);

      setStudentsWithNoTimeSubmitted(current => [...sortedCaseloads]);

      //Remove the loading indicator and return the caseloads
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
    return caseloads;
  }

  const keepStudent = (caseloadId: number): void => {
    const studentCaseload: caseloadDisplay = studentsWithNoTimeSubmitted.find(c => c.caseloadId === caseloadId);

    setDischargeCaseloads(current => current.filter(c => c.caseloadId !== caseloadId));
    setRemoveCaseloads(current => current.filter(c => c.caseloadId !== caseloadId));
    if (keepCaseloads.filter(c => c.caseloadId === studentCaseload.caseloadId).length === 0)
      setKeepCaseloads(current => [...current, studentCaseload]);

    //Hide the modal
    setDischargeModal(false);
  }

  const updateCaseloads = async () => {
    try {
      await batchUpdateCaseloads([...keepCaseloads, ...dischargeCaseloads, ...removeCaseloads]);
    } catch (error: any) {
      setSubmitMessage(typeof error === 'string' ? error : 'An error occurred while updating caseloads.');
      setIsSubmitSuccess(false);
    }
  };
  const reloadTimesheet = () => timesheetsRequestNoRefresh.run({ disciplineId: selectedDiscipline?.disciplineId, startDate: startDate, endDate: endDate });

  const getDayTotal = (week: TimesheetWeek, date: Date) => {
    let totalTime = 0;
    if (!week.items || week.items.length === 0)
      return 0;
    week.items?.forEach(x => {
      x.tasks?.forEach(t => {
        totalTime += t.entries?.filter(x => new Date(x.dateOfEntry).toDateString() === date.toDateString()).reduce((previous, { minutes }) => previous + (minutes ?? 0), 0) ?? 0;
      })
    })

    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    return formatter.format(totalTime / 60)
  }

  const handleCloseAlert = () => {
    setShowAlert(false);
  };

  return (
    <Layout isLoading={
      timesheetsRequest.isPending ||
      handleSubmitAsync.isLoading ||
      handleRequestTimesheetUnlockRequestAsync.isLoading ||
      loadDistricts.isPending ||
      loadDistrictProgramOptions.isPending ||
      isFetchingCaseload ||
      isLoading
    }
      isScrollable={false}>
      <div className={timesheetsRequest.isPending ? 'hidden' : 'block'}>
        {/* Modal: Where users can select the monnth they want to view */}
        <Modal
          open={openDateRangeFilter}
          saveText='Set Date Range'
          onSave={() => {
            setSelectedRange(null);
            setSelectedTimesheet(null);
            setIsLocked(false);

            if (hasValue(selectedDiscipline))
              timesheetsRequest.run({ disciplineId: selectedDisciplineId, startDate: startDate, endDate: endDate });
          }}
          onCancel={() => setOpenDateRangeFilter(false)}
        >
          <div className='flex space-x-3'>
            {/*submitMessage ? submitMessage : ''*/}
            <div className='w-full'>
              <label className='mb-2 no-wrap'>Start Date:</label>
              <DatePicker
                selected={startDate}
                onChange={(date) => {
                  if (date) {
                    const maxEndDate = new Date(date as Date);
                    maxEndDate.setDate(maxEndDate.getDate() + 90); // Set max end date to 90 days after new start date

                    // Check if current end date is within the new valid range
                    if (endDate && endDate > maxEndDate) {
                      setAlertMessage("The selected end date exceeds the 90-day limit from the new start date. Please choose a valid end date.");
                      setShowAlert(true)
                      setEndDate(null);
                    }
                  }
                  setStartDate(date as Date);
                }}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                maxDate={endDate}
                className="border p-2 rounded"
                placeholderText="Select a start date"
              />
            </div>
            <div className='w-full'>
              <label className='mb-2 no-wrap'>End Date:</label>
              <DatePicker
                selected={endDate}
                onChange={(date) => {
                  if (startDate && date) {
                    const maxEndDate = new Date(startDate);
                    maxEndDate.setDate(maxEndDate.getDate() + 90); // Add 90 days to start date

                    if (date > maxEndDate) {
                      setAlertMessage("End date cannot exceed 90 days from the start date.");
                      setShowAlert(true);
                      return;
                    }
                  }
                  setEndDate(date as Date)
                }}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate} // Prevents selecting an end date before the start date
                className="border p-2 rounded"
                placeholderText="Select an end date"
                disabled={!startDate}
              />
            </div>
          </div>
          {showAlert && (
            <CustomAlert
              message={alertMessage!}
              onClose={handleCloseAlert}
            />
          )}
        </Modal>
        {/* Modal: Where users can select the discipline they want to use */}
        <DisciplineModal
          key={`discipline-modal-${selectedDisciplineId}`}
          open={openDiscipline}
          toggle={setOpenDiscipline}
          disciplineId={state?.disciplineId ?? selectedDisciplineId}
          setDiscipline={(d) => {
            handleSetDiscipline(d);
            /* removing this for now for performance cost
            getStudentsWithNoTimeSubmitted().then(students => {
              setStudentsWithNoTimeSubmitted(current => [...students]);
            });
            */
          }}
          noInitialChange />
        {/* Modal: Where users can enter time for the selected week */}
        <LogtimeModal
          open={openLogtime}
          toggle={(o, r) => {
            if (o !== undefined) {
              setOpenLogtime(o);
            }
            if (r) {
              setSaveWeek(o !== undefined);
              timesheetsRequest.run({ disciplineId: selectedDiscipline?.disciplineId, startDate: startDate, endDate: endDate });
            }
          }}
          startDate={moment(selectedRange?.week?.firstDayOfWeek)}
          endDate={moment(selectedRange?.week?.lastDayOfWeek)}
          disciplineId={selectedDisciplineId ?? -1}
          districts={districts ?? []}
          districtProgramOptions={districtProgramOptions ?? []}
          selectedTimesheet={selectedRange?.week}
          editEntry={editEntry}
          timesheetsAsync={reloadTimesheet}
          allTaskTypes={allTaskTypes}
          resetModal={resetLogtimeModal}
          setResetModal={setResetLogtimeModal} />
        <Modal
          saveText='Ok'
          open={(flagNotes && flagNotes.length > 0) || timesheetFeedback !== ''}
          onCancel={() => { setFlagNotes(undefined); setTimesheetFeedback(''); }}
          onSave={() => { setFlagNotes(undefined); setTimesheetFeedback(''); }}
        >
          <Fragment>
            <ul>
              {timesheetFeedback && timesheetFeedback !== '' &&
                <li className="mb-2">"{timesheetFeedback}"<br />
                  <span className='pl-4 text-sm italic text-gray-500'>
                    ~ {timesheetFeedbackUser ?? ""} {timesheetFeedbackDate && moment(timesheetFeedbackDate).isValid() ? moment(timesheetFeedbackDate).fromNow() : ''}
                  </span>
                </li>}
              {flagNotes?.filter(n => n.note !== undefined && n.note !== "").map((note, i) => (
                <li key={"fnd_" + i}>
                  "{note.note}" <br />
                  <span className='pl-4 text-sm italic text-gray-500'>
                    ~ {note.authorLastName ? (note.authorLastName + ", ") : ""} {note.authorFirstName ?? ""} {moment(note.createdDate).fromNow()}
                  </span>
                </li>
              ))}
            </ul>
          </Fragment>
        </Modal>
        <DischargeModal
          saveText='Discharge'
          open={dischargeModal}
          onCancel={() => setDischargeModal(false)}
          onSave={() => {
            setDischargeCaseload(current => {
              const caseload: caseloadDisplay = dischargeCaseload!;
              caseload.dischargeDate = dischargeCaseloadEndDate;
              return caseload;
            });
            const studentCaseload: caseloadDisplay = studentsWithNoTimeSubmitted.find(c => c.caseloadId === dischargeCaseload?.caseloadId);
            setKeepCaseloads(current => current.filter(c => c.caseloadId !== dischargeCaseload?.caseloadId));
            setRemoveCaseloads(current => current.filter(c => c.caseloadId !== dischargeCaseload?.caseloadId));
            if (dischargeCaseloads.filter(c => c.caseloadId === studentCaseload.caseloadId).length === 0)
              setDischargeCaseloads(current => [...current, studentCaseload]);

            //Hide the modal
            setDischargeModal(current => false);
          }}
          caseload={dischargeCaseload!}
          disciplineEndDate={dischargeCaseloadEndDate ?? new Date()}
          setDisciplineEndDate={setDischargeCaseloadEndDate}
        />
        <DischargeModal
          isDischarge={false}
          saveText='Remove From Caseload'
          open={removeCaseloadModal}
          onCancel={() => setRemoveCaseloadModal(false)}
          onSave={() => {
            setRemoveCaseload(current => {
              const caseload: caseloadDisplay = removeCaseload!;
              caseload.removeDate = removeCaseloadEndDate;
              return caseload;
            });
            const studentCaseload: caseloadDisplay = studentsWithNoTimeSubmitted.find(c => c.caseloadId === removeCaseload?.caseloadId);
            setKeepCaseloads(current => current.filter(c => c.caseloadId !== removeCaseload?.caseloadId));
            setDischargeCaseloads(current => current.filter(c => c.caseloadId !== removeCaseload?.caseloadId));
            if (removeCaseloads.filter(c => c.caseloadId === studentCaseload.caseloadId).length === 0)
              setRemoveCaseloads(current => [...current, studentCaseload]);

            //Hide the modal
            setRemoveCaseloadModal(current => false);
          }}
          caseload={removeCaseload!}
          disciplineEndDate={removeCaseloadEndDate ?? new Date()}
          setDisciplineEndDate={setRemoveCaseloadEndDate}
        />
        <div className='flex'>
          {/* Left Column: Date Range picker */}
          <Fragment>
            <div className='space-y-5 w-72'>
              <div className='flex items-start justify-between discipline-picker'>
                {/* Discipline Picker */}
                <div onClick={() => { setOpenDiscipline(true); }}>
                  <span className='font-bold'>{selectedDiscipline?.name}</span>
                  <button className='block text-sm link'>
                    Change Discipline
                  </button>
                </div>
                {/* Date Range Picker */}
                <div className='flex flex-col items-center underline cursor-pointer hover:no-underline'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-7 w-7 text-ptsblue-normal'
                    viewBox='0 0 20 20'
                    fill='currentColor'
                  >
                    <path
                      fillRule='evenodd'
                      d='M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z'
                      clipRule='evenodd'
                    />
                  </svg>
                  <span
                    className='block text-sm text-center'
                    onClick={() => setOpenDateRangeFilter(true)}
                  >
                    Change
                    <br />
                    Date Range
                  </span>
                </div>
              </div>
              {/* Timesheet dates organized by Date Range */}
              <div className='space-y-5 w-72' style={{ maxHeight: '30em', overflowY: 'auto' }}>
                {timesheets?.map((ts, ti) => (
                  <div
                    key={`timesheet_${ti}`}
                    className='p-3 bg-white rounded-lg shadow timesheet'
                  >
                    <div className='flex items-center justify-center timesheet-status'>
                      {/* <span className='mr-5 font-bold'>{ts.title} </span> */}

                      {(ts.status?.timesheetStatusId === TimesheetStatus.InProgress || ts.status?.timesheetStatusId === TimesheetStatus.Rejected) && (
                        <button
                          className='py-1 text-sm bg-ptsgreen-normal hover:bg-ptsgreen-dark'
                          onClick={(e) => {

                            setReviewTimesheet(ts);
                            getTotalHours(ts);
                            setActiveTimesheet(ts);
                            setShowFlags(true);
                            setTsStatus(ts.status?.timesheetStatusId);
                            setIsReviewed(false);

                            getStudentsWithNoTimeSubmitted(ts).then(students => {
                              setIsInReview(current => students.length > 0);
                              setStudentsWithNoTimeSubmitted(current => [...students]);
                            });
                          }}
                        >
                          Review
                        </button>
                      )}


                      {/*
                      Removing the rejected button per Jake's request. They want to allow users to click on review without making changes.
                        NOTE: Lisa isn't aware of this change because she's on vacation when this request was made.
                      {(ts.status?.timesheetStatusId === TimesheetStatus.Rejected) && (
                        <button className='py-1 text-sm bg-red-600 hover:bg-red-800'>
                          Rejected
                        </button>
                      )}
                    */}


                      {ts.status?.timesheetStatusId === TimesheetStatus.Submitted && (
                        <button className='py-1 text-sm italic text-gray-500 bg-white hover:bg-white hover:text-black'>
                          Submitted
                        </button>
                      )}

                      {ts.status?.timesheetStatusId === TimesheetStatus.Approved && (
                        <button className='py-1 text-sm'>Approved</button>
                      )}

                      {ts.status === null && (
                        <button className='py-1 text-sm italic text-gray-500 bg-white hover:bg-white hover:text-black'>
                          Idle
                        </button>
                      )}
                    </div>

                    {(ts.status?.timesheetStatusId === TimesheetStatus.Rejected || ts.showFlags === true) && (
                      <div
                        className='p-2 mt-3 text-sm text-white bg-red-500 rounded-lg cursor-pointer'
                        onClick={() => {
                          setTimesheetFeedback(ts.feedback ?? "");
                          setTimesheetFeedbackDate(ts.feedbackDate);
                          setTimesheetFeedbackUser(ts.feedbackUser ?? '');
                          setFlagNotes(ts.flagNotes.filter(n => n.note !== undefined && n.note !== ""));
                          /*if (ts.flagNotes?.filter(n => n.note !== undefined && n.note !== "").length > 0) {
  
                            const tsFlags = ts.timesheetFlags?.filter(f => f.feedback && f.feedback !== '');
                            const tsFlagNotes: TimesheetFlagNote[] = [];
  
                            tsFlags?.map(f =>
                              tsFlagNotes.push({
                                note: f.feedback ?? '',
                                authorFirstName: ts.flagNotes.filter(n => n.note !== undefined && n.note !== "")[0].authorFirstName,
                                authorLastName: ts.flagNotes.filter(n => n.note !== undefined && n.note !== "")[0].authorLastName,
                                createdDate: f.dateFlagged,
                              }));
                            setFlagNotes([...ts.flagNotes.filter(n => n.note !== undefined && n.note !== ""), ...tsFlagNotes]);
                          }*/
                        }}>
                        {ts.flagNotes?.filter(n => n.note !== undefined && n.note !== "").length === 0 &&
                          ts.feedback === "" && (
                            <Fragment>
                              Your timesheet was rejected by a Billing Specialist but no feedback was entered. Please message your Billing Specialist for more details.
                            </Fragment>
                          )}

                        {(ts.flagNotes?.filter(n => n.note !== undefined && n.note !== "").length > 0 ||
                          ts.feedback !== "") && (
                            <Fragment>
                              Your timesheet was rejected by a Billing Specialist, please click here to view the feedback/corrections needed.
                            </Fragment>
                          )}
                      </div>
                    )}

                    <div className='mt-2 timesheet-weeks-nav'>
                      {ts.weeks.map((w, wi) => (
                        <div
                          key={`week_${wi}`}
                          className={cx(
                            wi % 2 !== 0 ? 'bg-gray-100' : 'bg-white',
                            'p-2 flex justify-between hover:bg-gray-200 cursor-pointer'
                          )}
                          onClick={() => {
                            if (ts) {
                              setTsStatus(ts.status?.timesheetStatusId ?? TimesheetStatus.InProgress);
                              setReviewTimesheet(undefined);
                              setTaskTypeFound(false)
                              setIsInReview(current => false);
                              setStudentsWithNoTimeSubmitted([]);
                              setShowFlags(true);
                              setActiveTimesheet(current => ts);
                              setSelectedRange({
                                startDate: new Date(),
                                endDate: new Date(),
                                week: w,
                              });
                              setSelectedTimesheet(ts);
                              setIsLocked(false);
                              if (moment(ts!.end).startOf('day').add(lockDateOffset, 'days').add(lockTimeOffset, 'hours') < moment.utc() && (!ts.status || ts.status?.timesheetStatusId != TimesheetStatus.Approved)) {
                                if (ts.timesheetId == null) {
                                  setIsLocked(true)
                                } else {
                                  setIsLocked(ts.isLocked!);
                                }
                              }
                              setSelectedWeek(current => w);
                              setIsReviewed(false);
                              //setExpandedWeek(-1);
                            }
                          }}
                        >
                          <div className={`text-sm ${ts?.showFlags && isWeekFlagged(w.items) ? 'font-bold text-red-600' : ''}`}>{w.title} {(w.items?.length > 0) ? <span className='italic'>({w.items.length})</span> : <span></span>}</div>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            className={cx(
                              'h-6 w-6 text-ptsgreen-dark',
                              w.hasIssues ? 'block' : 'hidden'
                            )}
                            viewBox='0 0 20 20'
                            fill='currentColor'
                          >
                            <path
                              fillRule='evenodd'
                              d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                              clipRule='evenodd'
                            />
                          </svg>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Fragment>

          {/* Right Column: Where timesheets are displayed */}
          {!reviewTimesheet &&
            !isInReview &&
            (studentsWithNoTimeSubmitted.length !==
              keepCaseloads.length + dischargeCaseloads.length + removeCaseloads.length || studentsWithNoTimeSubmitted.length === 0
            ) && (
              <div className='flex-grow p-3 ml-5 bg-white rounded-lg shadow'>
                {submitMessage && (
                  <Notification
                    message={submitMessage}
                    isSuccess={isSubmitSuccess}
                    isOpen={submitMessage !== '' && submitMessage !== undefined && submitMessage !== null}
                    onClose={() => setSubmitMessage('')}
                  />
                )}

                {!selectedRange && <div className='text-center text-gray-500'>Select a week to begin.</div>}
                {selectedRange && (
                  <div>
                    {/* Timesheet Flags */}
                    {activeTimesheet &&
                      activeTimesheet.timesheetFlags !== null &&
                      activeTimesheet.timesheetFlags?.filter((timesheetFlag: TimesheetFlag) => timesheetFlag.timesheetWeekId === selectedWeek?.weekId).length! > 0 &&
                      activeTimesheet.showFlags &&
                      showFlags &&
                      <Notification isSuccess={false} isUserCloseable={false}>
                        <ul className='pl-5 list-disc'>
                          {activeTimesheet.timesheetFlags
                            ?.filter((timesheetFlag: TimesheetFlag) => timesheetFlag.timesheetWeekId === selectedWeek?.weekId)
                            ?.map((timesheetFlag: TimesheetFlag) => (
                              <Fragment key={`timesheet-flag-${timesheetFlag.timesheetFlagId}`}>
                                {((timesheetFlag && timesheetFlag !== undefined) &&
                                  (timesheetFlag.proofingRule !== null) ?
                                  <li>
                                    <span
                                      className='font-bold text-white underline cursor-pointer hover:text-black'
                                      onClick={() => {
                                        handleTimesheetFlagModal(timesheetFlag);
                                      }}>
                                      {timesheetFlag.proofingRule?.name ?? 'Proofing rule not found'}
                                    </span>
                                    <span> -- Created: {moment(timesheetFlag.dateFlagged).format('l')}</span>
                                  </li>
                                  :
                                  <></>
                                )
                                }
                              </Fragment>
                            ))}
                        </ul>
                      </Notification>
                    }
                    {activeTimesheet &&
                      activeTimesheet.flags !== null &&
                      (activeTimesheet.flags?.length ?? 0) > 0 &&
                      activeTimesheet.showFlags &&
                      showFlags &&
                      activeTimesheet.flags?.filter((timesheetFlag: TimesheetEntryFlag) => timesheetFlag.timesheetWeekId === selectedWeek?.weekId).length! > 0 &&
                      <Notification isSuccess={false} isUserCloseable={false}>
                        <ul className='pl-5 list-disc'>
                          {activeTimesheet.flags?.filter((timesheetFlag: TimesheetEntryFlag) => timesheetFlag.timesheetWeekId === selectedWeek?.weekId)
                            ?.map((timesheetFlag: TimesheetEntryFlag) => (
                              <Fragment key={`timesheet-flag-${timesheetFlag.timesheetEntryFlagId}`}>
                                {activeTimesheet.flags?.filter(f => {
                                  return moment(f.dateFlagged).isBetween(selectedRange?.week?.firstDayOfWeek, selectedRange?.week?.firstDayOfWeek);
                                })}
                                {((timesheetFlag && timesheetFlag !== undefined) &&
                                  (timesheetFlag.proofingRule !== null) ?
                                  <li>
                                    <span
                                      className='font-bold text-white underline cursor-pointer hover:text-black'
                                      onClick={() => {
                                        handleTimesheetFlagModal(timesheetFlag);
                                      }}>
                                      {timesheetFlag.proofingRule?.name ?? 'Proofing rule not found'}
                                    </span>
                                    <span> -- Created: {moment(timesheetFlag.dateFlagged).format('l')}</span>
                                  </li>
                                  :
                                  <></>
                                )
                                }
                              </Fragment>
                            ))}
                        </ul>
                      </Notification>
                    }
                    <div>
                      <div className={`inline-block w-1/${isLocked ? 3 : 2}`}>
                        <div className='h-3 text-sm text-gray-500'>Viewing:</div>
                        <div className='text-2xl font-bold h-7'>{selectedRange?.week?.title}</div>
                      </div>
                      {selectedTimesheet && isLocked && (
                        <div className={`inline-block w-1/${isLocked ? 3 : 2}`} style={{ textAlign: 'center' }}>
                          <div className='text-2xl font-bold' style={{ color: '#f59e57' }}>Period Locked</div>
                          {
                            !selectedTimesheet.timesheetUnlockRequests?.some(x => x.timesheetUnlockStatus === TimesheetUnlockStatusLookup.Pending) &&
                            (
                              <div>
                                <a style={{ color: 'cornflowerblue', cursor: 'pointer' }}
                                  onClick={() => handleRequestTimesheetUnlockRequestAsync.run()}>
                                  Request Unlock
                                </a>
                              </div>
                            )
                          }
                          {
                            selectedTimesheet.timesheetUnlockRequests?.some(x => x.timesheetUnlockStatus === TimesheetUnlockStatusLookup.Pending) &&
                            (
                              <div>
                                <p style={{ color: 'red', cursor: 'pointer' }}>Request has been sent</p>
                              </div>
                            )
                          }
                          <div></div>
                        </div>
                      )
                      }
                      {
                        moment(selectedRange?.week?.firstDayOfWeek) <= endOfCurrentWeek &&
                        <div className={`inline-block w-1/${isLocked ? 3 : 2} align-top`}>
                          <button
                            className='float-right py-1 text-sm bg-ptsgreen-normal hover:bg-ptsgreen-dark'
                            onClick={() => { setResetLogtimeModal(true); setEditEntry(undefined); setOpenLogtime(true) }}
                            disabled={tsStatus === TimesheetStatus.Submitted || tsStatus === TimesheetStatus.Approved || isLocked}
                          >
                            Enter Time
                          </button>
                        </div>
                      }
                    </div>

                    <TimesheetWeekEntries
                      week={selectedRange?.week!}
                      displayFlags={activeTimesheet?.showFlags}
                      allowEdit={tsStatus !== TimesheetStatus.Submitted && tsStatus !== TimesheetStatus.Approved}
                      editEntry={(editModel) => { setEditEntry(editModel); setOpenLogtime(true) }}
                      timesheet={activeTimesheet}
                      districts={districts} />
                  </div>
                )}
              </div>
            )}

          {/* Right Column: Pre Review Validation */}
          {reviewTimesheet &&
            isInReview &&
            studentsWithNoTimeSubmitted.length > 0 && (
              <div className='flex-grow p-3 ml-5 bg-white rounded-lg shadow'>
                <h2 className="float-left mb-4 font-bold text-center">Students With No Time Submitted</h2>
                <h2 className="float-left pl-2 mb-4 font-bold text-center ml-36">
                  Caseload Cleanup
                  <span className="block text-sm font-normal">(please indicate the status of the students listed)</span>
                </h2>
                <ul className={`clear-both pt-4 mt-4 h-6/12 overflow-hidden${studentsWithNoTimeSubmitted.length > 9 ? ` overflow-y-scroll scrollbar` : ` no-scrollbar overflow-y-hidden`}`} style={{ maxHeight: '60%' }}>
                  {[...studentsWithNoTimeSubmitted].map((caseload: caseloadDisplay, i: number) =>
                    <li
                      key={`student-with-no-time-submitted-${caseload.caseloadId}`}
                      className={`relative max-h-10 ${i < studentsWithNoTimeSubmitted.length - 1 ? ` mb-4 pb-10 border-b` : ``}`}
                    >
                      <span className="student-name">{`${caseload.studentLastName}, ${caseload.studentFirstName}`}</span>
                      <div className={`relative float-right bottom-2.5`}>
                        <button
                          className={`mr-2 btn 
                            ${keepCaseloads.filter(c => caseload.caseloadId === c.caseloadId).length === 0 ?
                              (removeCaseloads.filter(c => caseload.caseloadId === c.caseloadId).length > 0 || dischargeCaseloads.filter(c => caseload.caseloadId === c.caseloadId).length > 0 ? 'bg-gray-500 hover:bg-gray-700' : 'btn-primary') : 'btn-primary'
                            }`}
                          onClick={() => keepStudent(caseload.caseloadId)}
                        >Keep on Caseload</button>
                        <button
                          className={`mr-2 text-white
                            ${removeCaseloads.filter(c => caseload.caseloadId === c.caseloadId).length === 0 ?
                              (keepCaseloads.filter(c => caseload.caseloadId === c.caseloadId).length > 0 || dischargeCaseloads.filter(c => caseload.caseloadId === c.caseloadId).length > 0 ? 'bg-gray-500 hover:bg-gray-700' : 'bg-yellow-500 hover:bg-yellow-600') : 'bg-yellow-500 hover:bg-yellow-500'
                            }`}
                          onClick={() => {
                            setRemoveCaseload(caseload);
                            setRemoveCaseloadModal(true);
                          }}
                        >Remove from Caseload</button>
                        <button
                          className={`mr-2 text-white 
                            ${dischargeCaseloads.filter(c => caseload.caseloadId === c.caseloadId).length === 0 ?
                              (keepCaseloads.filter(c => caseload.caseloadId === c.caseloadId).length > 0 || removeCaseloads.filter(c => caseload.caseloadId === c.caseloadId).length > 0 ? 'bg-gray-500 hover:bg-gray-700' : 'bg-red-500 hover:bg-red-600') : 'bg-red-500 hover:bg-red-500'
                            }`}
                          onClick={() => {
                            setDischargeCaseload(caseload);
                            setDischargeModal(true);
                          }}
                        >Discharge</button>
                      </div>
                      <div className="block clear-both"></div>
                    </li>
                  )}
                </ul>
                <div className="relative pt-4 noTimeSubmitted-footer">
                  <button
                    className='absolute left-0 mr-2 btn btn-primary bg-ptsgreen-normal hover:bg-ptsgreen-dark'
                    disabled={!(studentsWithNoTimeSubmitted.length > (keepCaseloads.length + dischargeCaseloads.length + removeCaseloads.length))}
                    onClick={() => {
                      const studentsSelected: caseloadDisplay[] = [...dischargeCaseloads, ...removeCaseloads];
                      const studentsNotSelected: caseloadDisplay[] = studentsWithNoTimeSubmitted.filter(c => studentsSelected.filter(x => x.caseloadId === c.caseloadId).length === 0);
                      setKeepCaseloads(current => [...current, ...studentsNotSelected]);
                    }}>Keep remaining students on caseload</button>
                  <button
                    className='absolute right-0 mr-2 btn btn-primary'
                    disabled={studentsWithNoTimeSubmitted.length > (keepCaseloads.length + dischargeCaseloads.length + removeCaseloads.length)}
                    onClick={() => {
                      setIsInReview(current => false);
                      updateCaseloads();
                      setTimeout(() => {
                        setOverviewHeader()
                      }, 300)
                    }}>Continue Timesheet Submission</button>
                </div>
              </div>
            )}

          {/* Right Column: Review timesheet display */}
          {reviewTimesheet &&
            !isInReview &&
            (!(studentsWithNoTimeSubmitted.length > (keepCaseloads.length + dischargeCaseloads.length + removeCaseloads.length)) || studentsWithNoTimeSubmitted.length === 0) && (
              <div id="timesheetOverviewContainer" className='flex-grow p-3 ml-5 bg-white rounded-lg shadow'>
                {setOverviewHeader()}
                <div className='text-3xl text-gray-500'>Timesheet Overview</div>

                <div className='ml-1'>
                  <b>Total Hours Logged:</b> {totalHours}h <br />
                  <b>Date of submission:</b> {moment().format("MM/DD/YYYY")} <br />
                </div>
                <br />

                {reviewTimesheet &&
                  <div className='ml-1'>
                    {(() => {
                      const hasExistingWeekendEntries = reviewTimesheet.weeks.some(w =>
                        w.items.some(item =>
                          item.tasks.some(task =>
                            task.entries.some(entry => {
                              const d = moment(entry.dateOfEntry).isoWeekday();
                              return (d === 6 || d === 7) && (entry.minutes ?? 0) > 0;
                            })
                          )
                        )
                      );
                      const showWeekend = (districts?.some(d => d.enableWeekendBilling) ?? false) || hasExistingWeekendEntries;
                      const totalDays = showWeekend ? 7 : 5;
                      return (
                      <>
                    <div><h1 style={{ fontWeight: 'bold' }}>Period Overview (Hours)</h1></div>
                    <table style={{ maxHeight: '30rem', width: '100%' }}>
                      <tr className={'bg-ptsgreen-normal'} style={{ border: '1px solid', color: 'white' }}>
                        <th style={{ border: '1px solid black' }}>Mon</th>
                        <th style={{ border: '1px solid black' }}>Tue</th>
                        <th style={{ border: '1px solid black' }}>Wed</th>
                        <th style={{ border: '1px solid black' }}>Thu</th>
                        <th style={{ border: '1px solid black' }}>Fri</th>
                        {showWeekend && <th style={{ border: '1px solid black' }}>Sat</th>}
                        {showWeekend && <th style={{ border: '1px solid black' }}>Sun</th>}
                      </tr>
                      {reviewTimesheet.weeks.map(week => {
                        let firstDay = moment(new Date(week.firstDayOfWeek));
                        let firstDayNumber = firstDay.day();
                        let dates: Moment[] = [];

                        if (firstDayNumber && firstDayNumber > 1) {
                          let daysInc = totalDays;
                          while (daysInc > 0) {
                            daysInc--;
                            firstDayNumber--;
                            dates.push(moment(firstDay).add(-firstDayNumber, 'd'))
                          }
                        } else {
                          while (firstDayNumber <= totalDays) {
                            dates.push(moment(firstDay).add(firstDayNumber - 1, 'd'))
                            firstDayNumber++;
                          }
                        }

                        return (
                          <tr>
                            {dates.map(date => {
                              return (
                                <td style={{ textAlign: 'center', border: '1px solid', backgroundColor: 'white' }}>
                                  <div>
                                    <p style={{ borderBottom: '1px solid', fontWeight: 'bold' }}>
                                      {date.format('D-MMM')}
                                    </p>
                                    <p>
                                      {getDayTotal(week, date.toDate())}
                                    </p>
                                  </div>
                                </td>
                              )
                            })}
                          </tr>
                        )
                      })}
                    </table>

                    <br />
                      </>
                      );
                    })()}
                  </div>
                }

                <div className="overviewScrollablePart">
                  {groupedWeek &&
                    groupedWeek?.sort((a, b) => a.title?.localeCompare(b.title)).map((district, districtIndex) => {
                      if (district.schools?.length! > 0) {

                        return (
                          <div key={districtIndex}>
                            <h1><span style={{ fontWeight: 'bold' }}>{district.title}</span> (Total Time {getDistrictTotalTime(district.schools!)}hrs.)</h1>
                            <br />
                            {district.schools?.sort((a, b) => a.title?.localeCompare(b.title)).map((school, schoolIndex) => {
                              if (school.items?.length! > 0) {
                                const itemIndex = `${districtIndex}_${schoolIndex}`;
                                const taskType = {
                                  Absent: 'Absence',
                                  UnAvailable: 'Unavailable'
                                }
                                return (
                                  <div key={itemIndex}>
                                    <h3 style={{ fontWeight: 'bold' }}>{school.title}</h3>
                                    <hr style={{ border: '1px solid black' }} />
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1.5fr 1fr 1fr', backgroundColor: 'lightgray', fontWeight: 'bold' }}>
                                      <p>Date</p>
                                      <p>Entry</p>
                                      <p>Entry Type</p>
                                      <p>Minutes</p>
                                      <p>Notes</p>
                                    </div>

                                    {school.items?.map((entry, entryIndex) => {
                                      const itemIndex = `${districtIndex}_${schoolIndex}_${entryIndex}`;


                                      return (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1.5fr 1fr 1fr', lineHeight: '34px' }}>
                                          <p>{entry.dateString}</p>
                                          <p>{entry.entry}</p>
                                          <p>{entry.entryType}</p>
                                          <p>{entry.minutes}</p>
                                          <p>{entry.notes}</p>
                                        </div>

                                      )
                                    })
                                    }
                                    <hr />
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1.5fr 1fr 1fr', fontWeight: 'bold' }}>
                                      <div></div>
                                      <div></div>
                                      <div>{school.title} Total</div>
                                      <div>{school.items?.reduce((n, { minutes }) => n + minutes, 0)}</div>
                                      <div></div>
                                    </div>
                                    <br />
                                  </div>
                                )
                              }
                            })
                            }
                          </div>
                        )
                      }
                    })
                  }

                  <div className='mt-3'>
                    <button
                      title={totalHours! <= 0 ? "Zero Timesheet Entry can not be submitted" : ""}
                      disabled={!isReviewed}
                      onClick={(e) => { handleSubmitAsync.run() }}
                    >
                      Submit Timesheet
                    </button>
                    <input
                      className='ml-2 mr-1'
                      type="checkbox"
                      checked={isReviewed}
                      onChange={(e) => {
                        if (totalHours! > 0) {

                          setIsReviewed(e.currentTarget.checked!);
                        }
                        if (totalHours! <= 0 && taskTypeFound) {
                          setIsReviewed(e.currentTarget.checked!);
                        }
                      }}
                    />
                    <span>I reviewed the timesheet</span>
                  </div>
                </div>
              </div>
            )
          }
        </div>
      </div >
    </Layout >
  );
};

export default Timesheets;
